// frontend/shared/hooks/useMessage.ts
import { message, MessageArgsProps } from 'antd';
import { useCallback } from 'react';
import { useTheme } from '@/components/common/ThemeProvider';

interface MessageConfig extends MessageArgsProps {
    duration?: number;
}

interface UseMessageReturn {
    success: (content: string, config?: MessageConfig) => void;
    error: (content: string, config?: MessageConfig) => void;
    info: (content: string, config?: MessageConfig) => void;
    warning: (content: string, config?: MessageConfig) => void;
    loading: (content: string, config?: MessageConfig) => void;
}

export const useMessage = (): UseMessageReturn => {
    const { dark } = useTheme();
    const defaultDuration = 3; // 3 seconds

    const getClassName = useCallback(() => {
        return dark ? 'dark-mode-message' : '';
    }, [dark]);

    const createMessage = useCallback((
        type: 'success' | 'error' | 'info' | 'warning' | 'loading',
        content: string,
        config?: MessageConfig
    ) => {
        const className = getClassName();
        message[type]({
            content,
            className,
            duration: config?.duration ?? defaultDuration,
            style: {
                background: dark ? '#1f1f1f' : '#ffffff',
                boxShadow: dark ? '0 3px 6px -4px rgba(0,0,0,0.48)' : undefined,
            },
            ...config,
        });
    }, [dark, getClassName]);

    return {
        success: useCallback((content: string, config?: MessageConfig) =>
            createMessage('success', content, config), [createMessage]),

        error: useCallback((content: string, config?: MessageConfig) =>
            createMessage('error', content, config), [createMessage]),

        info: useCallback((content: string, config?: MessageConfig) =>
            createMessage('info', content, config), [createMessage]),

        warning: useCallback((content: string, config?: MessageConfig) =>
            createMessage('warning', content, config), [createMessage]),

        loading: useCallback((content: string, config?: MessageConfig) =>
            createMessage('loading', content, config), [createMessage]),
    };
};